import { IconCalendarEvent, IconHelpCircle, IconShoppingBag, IconSparkles, IconUser } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import {
	aiGenerationCreditCosts,
	type LandingPageAdditionalPageType,
	landingPageAdditionalPageTypes,
} from "@forento/core/models/ai"
import { type PlatformLanguageId, platformLanguageIds, platformLanguages } from "@forento/core/models/platform"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import AiImagePicker from "~/components/AiImagePicker"
import Button, { SubmitButton } from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import { InputTextArea } from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import useFileUpload from "~/hooks/useFileUpload"
import { captureForentoAiEvent, getBuyAiCreditsUrl, useForentoAiModalTracking } from "~/utilities/forentoAiAnalytics"
import { query } from "~/utilities/trpc"

const languageItems = platformLanguageIds.map(id => ({ id, title: platformLanguages[id].label }))

const AI_INPUT_LIMITS = { pageGoal: 500, contentToInclude: 1_000, supportingImages: 8 } as const

const pageTypeConfig = {
	"about-me": {
		title: "About Me",
		description: "Creator story, values, and credibility",
		contentLabel: "About yourself",
		contentPlaceholder:
			"Your background, expertise, teaching style, story, values, and anything learners should know",
		goalPlaceholder: "Build trust and help visitors understand why they should learn from me",
		Icon: IconUser,
	},
	"sales-page": {
		title: "Sales Page",
		description: "A focused page for an offer",
		contentLabel: "About the offer",
		contentPlaceholder:
			"The offer, who it is for, outcomes, bonuses, guarantees, price notes, or objections to address",
		goalPlaceholder: "Explain this offer clearly and guide visitors toward buying or signing up",
		Icon: IconShoppingBag,
	},
	"event-calendar": {
		title: "Event Calendar",
		description: "Upcoming events and reasons to attend",
		contentLabel: "About your events",
		contentPlaceholder:
			"Event themes, formats, schedule notes, audience, location, preparation, or what attendees get",
		goalPlaceholder: "Show upcoming events and encourage visitors to register",
		Icon: IconCalendarEvent,
	},
	other: {
		title: "Other",
		description: "A custom page idea",
		contentLabel: "Content to include",
		contentPlaceholder: "Tell Forento AI what sections, details, products, proof, or questions to include",
		goalPlaceholder: "Describe what this page should help visitors do or understand",
		Icon: IconHelpCircle,
	},
} satisfies Record<
	LandingPageAdditionalPageType,
	{
		title: string
		description: string
		contentLabel: string
		contentPlaceholder: string
		goalPlaceholder: string
		Icon: FC<{ className?: string }>
	}
>

const pageTypeItems = landingPageAdditionalPageTypes.map(id => ({ id, ...pageTypeConfig[id] }))

type Props = {
	isOpen: boolean
	defaultLanguage: PlatformLanguageId
	onClose(): void
	onGenerated(pageId: number, generationId: number): Promise<void>
}
const AiCreatePageModal: FC<Props> = ({ isOpen, defaultLanguage, onClose, onGenerated }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!
	const inputLimits = user.user?.isImpersonating ? undefined : AI_INPUT_LIMITS

	const [pageType, setPageType] = useState<LandingPageAdditionalPageType>("about-me")
	const [pageGoal, setPageGoal] = useState("")
	const [contentToInclude, setContentToInclude] = useState("")
	const [backgroundImage, setBackgroundImage] = useState<File | null>(null)
	const [supportingImages, setSupportingImages] = useState<File[]>([])
	const [language, setLanguage] = useState<PlatformLanguageId>(defaultLanguage)

	const imageUpload = useFileUpload()
	const generate = query.ai.generateLandingPageAdditionalPage.useMutation({
		async onSuccess(result) {
			captureForentoAiEvent("forento_ai_generation_succeeded", { type: "landing-page-additional-page" })
			await onGenerated(result.pageId, result.generationId)
			await user.reload()
			reset()
			onClose()
		},
		async onError(error) {
			console.error(error)
			captureForentoAiEvent("forento_ai_generation_failed", { type: "landing-page-additional-page" })
			await alert.show("Error", "Failed to generate page. Please try again.")
		},
	})

	useEffect(() => {
		if (isOpen) setLanguage(defaultLanguage)
	}, [defaultLanguage, isOpen])

	const selectedConfig = pageTypeConfig[pageType]
	const generationCost = aiGenerationCreditCosts["landing-page-additional-page"]
	const hasEnoughCredits = platform.aiCreditBalance >= generationCost
	useForentoAiModalTracking({
		type: "landing-page-additional-page",
		isFormVisible: isOpen,
		creditBalance: platform.aiCreditBalance,
		cost: generationCost,
	})
	const isGenerating = imageUpload.isPending || generate.isPending
	const canGenerate =
		pageGoal.trim().length > 0 && contentToInclude.trim().length > 0 && hasEnoughCredits && !isGenerating

	const handleGenerate = async () => {
		if (!canGenerate) return
		captureForentoAiEvent("forento_ai_generation_submitted", { type: "landing-page-additional-page" })

		let backgroundImageUrl: string | null = null
		const supportingImageUrls: string[] = []
		try {
			if (backgroundImage !== null) {
				const uploaded = await imageUpload.upload({ file: backgroundImage, kind: "landing-image" })
				if (uploaded === null) return
				backgroundImageUrl = uploaded.publicUrl
			}
			for (const file of supportingImages) {
				const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
				if (uploaded === null) return
				supportingImageUrls.push(uploaded.publicUrl)
			}
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to upload the images. Please try again.")
			return
		}

		generate.mutate({
			pageType,
			pageGoal,
			contentToInclude,
			backgroundImageUrl,
			supportingImageUrls,
			language,
		})
	}

	const reset = () => {
		setPageType("about-me")
		setPageGoal("")
		setContentToInclude("")
		setBackgroundImage(null)
		setSupportingImages([])
		setLanguage(defaultLanguage)
	}

	return (
		<DefaultModal
			width={760}
			isOpen={isOpen}
			onSubmit={handleGenerate}
			onClose={
				isGenerating
					? undefined
					: () => {
							reset()
							onClose()
						}
			}
		>
			<ModalTitle>Generate page with Forento AI</ModalTitle>
			<ModalText>Add a new page without changing your existing pages.</ModalText>
			<ModalInputs>
				<div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:grid-cols-3">
					<div>
						<div className="text-xs text-gray-500">Available balance</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(platform.aiCreditBalance)} credits
						</div>
					</div>
					<div>
						<div className="text-xs text-gray-500">This generation costs</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(generationCost)} credits
						</div>
					</div>
					<div className="flex items-center sm:justify-end">
						<Button
							variant="secondary"
							onClick={getBuyAiCreditsUrl("landing-page-additional-page")}
							className="w-full sm:w-auto"
						>
							Purchase credits
						</Button>
					</div>
					{!hasEnoughCredits && (
						<p className="text-sm text-red-600 sm:col-span-3">
							Purchase more credits before generating a page.
						</p>
					)}
				</div>
				<div className="grid gap-2 sm:grid-cols-2">
					{pageTypeItems.map(item => {
						const Icon = item.Icon
						const isSelected = item.id === pageType
						return (
							<button
								key={item.id}
								type="button"
								className={cn(
									"flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-colors",
									isSelected
										? "border-primary bg-primary/5 ring-primary/20 text-gray-950 ring-1"
										: "hover:border-primary/40 border-black/10 bg-white text-gray-700 hover:bg-gray-50",
								)}
								onClick={() => setPageType(item.id)}
							>
								<div
									className={cn(
										"flex size-10 shrink-0 items-center justify-center rounded-lg",
										isSelected ? "bg-primary text-white" : "bg-gray-100 text-gray-500",
									)}
								>
									<Icon className="size-5" />
								</div>
								<div className="min-w-0">
									<div className="text-sm font-semibold">{item.title}</div>
									<div className="text-xs leading-relaxed text-gray-500">{item.description}</div>
								</div>
							</button>
						)
					})}
				</div>

				<InputTextArea
					label="Page goal"
					value={pageGoal}
					onChange={setPageGoal}
					placeholder={selectedConfig.goalPlaceholder}
					maxCharacters={inputLimits?.pageGoal}
				/>
				<InputTextArea
					label={selectedConfig.contentLabel}
					value={contentToInclude}
					onChange={setContentToInclude}
					placeholder={selectedConfig.contentPlaceholder}
					maxCharacters={inputLimits?.contentToInclude}
				/>
				<div className="grid gap-4 sm:grid-cols-2">
					<AiImagePicker
						label="Background image"
						description="Optional image for the hero section"
						files={backgroundImage ? [backgroundImage] : []}
						onChange={files => setBackgroundImage(files[0] ?? null)}
					/>
					<AiImagePicker
						label="Supporting images"
						description="Optional images for gallery and content sections"
						files={supportingImages}
						onChange={setSupportingImages}
						maxFiles={inputLimits?.supportingImages}
						multiple
					/>
				</div>
				<Dropdown
					label="Language"
					items={languageItems}
					selectedItemId={language}
					onChange={id => setLanguage(id as PlatformLanguageId)}
				/>
			</ModalInputs>
			<ModalButtons>
				<Button
					variant="secondary"
					onClick={() => {
						reset()
						onClose()
					}}
					isDisabled={isGenerating}
				>
					Cancel
				</Button>
				<SubmitButton variant="primary" isDisabled={!canGenerate} isLoading={isGenerating}>
					<IconSparkles className="size-4" />
					Generate page ({toNumberFormat(generationCost)} credits)
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

export default AiCreatePageModal
